import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as ActionAPI from 'api/Action';
import { dispatch } from 'store';
import { TYPE_OF_FIELD_IN_COLUMN } from 'utils/scorecard';
import { openSnackbar } from 'store/reducers/snackbar';
import { PAGES } from 'sections/Action/constants';

const displayMessage = ({ type, msg }) => {
  dispatch(
    openSnackbar({
      open: true,
      message: msg ? msg : type === 'success' ? 'Success' : 'Something went wrong',
      variant: 'alert',
      alert: {
        color: type || 'success'
      },
      close: false
    })
  );
};

const initialState = {
  selectedPage: PAGES.MAIN_PAGE,
  action: {
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Name', key: 'name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Key', key: 'key', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Scope', key: 'scope', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Subscription Plans', key: 'subscription_plan_type', field_type: TYPE_OF_FIELD_IN_COLUMN.ARRAY, width: 120 },
      { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
    ],
    data: null
  },
  actionPoints: {
    selectedAction: null,
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Name', key: 'name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Priority Index', key: 'priority_index', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Key', key: 'key', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Value Type', key: 'value_type', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
    ],
    data: null
  },
  customerAction: {
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Name', key: 'agent_core_action.name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Key', key: 'agent_core_action.key', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Scope', key: 'agent_core_action.scope', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      {
        title: 'Subscription Plans',
        key: 'agent_core_action.subscription_plan_type',
        field_type: TYPE_OF_FIELD_IN_COLUMN.ARRAY,
        width: 120
      },
      { title: 'Status', key: 'status', field_type: TYPE_OF_FIELD_IN_COLUMN.STATUS, width: 120 },
      { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
    ],
    data: null,
    allPublicActions: []
  },
  status: '',
  ui: {
    loading: false,
    msg: ''
  }
};

const getActionData = createAsyncThunk('action/getActionData', async ({ params, signal }) => {
  const res = await ActionAPI.getActionData(params, signal);
  return res?.data?.data;
});

const createAction = createAsyncThunk('action/createAction', async (params) => {
  try {
    const res = await ActionAPI.createAction(params);
    displayMessage({ type: 'success', msg: 'Created successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateAction = createAsyncThunk('action/updateAction', async (params) => {
  try {
    const res = await ActionAPI.updateAction(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteAction = createAsyncThunk('action/deleteAction', async (id) => {
  try {
    const res = await ActionAPI.deleteAction(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const getActionPointsData = createAsyncThunk('action/getActionPointsData', async ({ params, signal }) => {
  const res = await ActionAPI.getActionPointsData(params, signal);
  return res?.data?.data;
});

const createActionPoints = createAsyncThunk('action/createActionPoints', async (params) => {
  try {
    const res = await ActionAPI.createActionPoints(params);
    displayMessage({ type: 'success', msg: 'Created successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateActionPoints = createAsyncThunk('action/updateActionPoints', async (params) => {
  try {
    const res = await ActionAPI.updateActionPoints(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteActionPoints = createAsyncThunk('action/deleteActionPoints', async (id) => {
  try {
    const res = await ActionAPI.deleteActionPoints(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

//Customer
const getCustomerActionData = createAsyncThunk('action/getCustomerActionData', async ({ params, signal }) => {
  const res = await ActionAPI.getCustomerActionData(params, signal);
  return res?.data?.data;
});

const createCustomerAction = createAsyncThunk('action/createCustomerAction', async (params) => {
  try {
    const res = await ActionAPI.createCustomerAction(params);
    displayMessage({ type: 'success', msg: 'Attached successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateCustomerAction = createAsyncThunk('action/updateCustomerAction', async (params) => {
  try {
    const res = await ActionAPI.updateCustomerAction(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteCustomerAction = createAsyncThunk('action/deleteCustomerAction', async (id) => {
  try {
    const res = await ActionAPI.deleteCustomerAction(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const getAllPublicActions = createAsyncThunk('action/getAllPublicActions', async () => {
  const res = await ActionAPI.getAllPublicActions();
  return res?.data?.data;
});

const ActionSlice = createSlice({
  name: 'action',
  initialState: initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    setSelectedPage: (state, action) => {
      state.selectedPage = action.payload;
    },
    //Action Tab
    setActionCurrentPage: (state, action) => {
      state.action.pagination.currentPage = action.payload;
    },
    setActionPageSize: (state, action) => {
      state.action.pagination.pageSize = action.payload;
    },
    setSelectedActionData: (state, action) => {
      state.actionPoints.selectedAction = action.payload;
    },

    //Action Point Tab
    setActionPointsCurrentPage: (state, action) => {
      state.actionPoints.pagination.currentPage = action.payload;
    },
    setActionPointsPageSize: (state, action) => {
      state.actionPoints.pagination.pageSize = action.payload;
    },

    //Customer
    setCustomerActionCurrentPage: (state, action) => {
      state.customerAction.pagination.currentPage = action.payload;
    },
    setCustomerActionPageSize: (state, action) => {
      state.customerAction.pagination.pageSize = action.payload;
    }
  },
  extraReducers: {
    [getAllPublicActions.fulfilled]: (state, action) => {
      state.customerAction.allPublicActions = action.payload;
    },
    [getActionData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getActionData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.action.pagination.totalRecords = action.payload?.count;
      state.action.data = action.payload?.results || [];
    },
    [getActionData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createAction.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createAction.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createAction.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateAction.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateAction.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      const updatedData = state.action.data.map((item) => {
        if (item.id === action.payload.id) {
          return action.payload;
        }
        return item;
      });
      state.action.data = updatedData;
    },

    [updateAction.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteAction.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteAction.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteAction.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [getActionPointsData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getActionPointsData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.actionPoints.pagination.totalRecords = action.payload?.count;
      state.actionPoints.data = action.payload?.results || [];
    },
    [getActionPointsData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createActionPoints.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createActionPoints.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createActionPoints.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateActionPoints.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateActionPoints.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      const updatedData = state.actionPoints.data.map((item) => {
        if (item.id === action.payload.id) {
          return action.payload;
        }
        return item;
      });
      state.actionPoints.data = updatedData;
    },

    [updateActionPoints.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteActionPoints.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteActionPoints.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteActionPoints.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [getCustomerActionData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getCustomerActionData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.customerAction.pagination.totalRecords = action.payload?.count;
      state.customerAction.data = action.payload?.results || [];
    },
    [getCustomerActionData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createCustomerAction.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createCustomerAction.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createCustomerAction.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateCustomerAction.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateCustomerAction.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [updateCustomerAction.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteCustomerAction.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteCustomerAction.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteCustomerAction.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    }
  }
});

const {
  resetState,
  setSelectedPage,
  setActionCurrentPage,
  setActionPageSize,
  setSelectedActionData,
  setActionPointsCurrentPage,
  setActionPointsPageSize,
  setCustomerActionCurrentPage,
  setCustomerActionPageSize
} = ActionSlice.actions;

export default ActionSlice.reducer;

export {
  resetState,
  setSelectedPage,
  //Action
  setActionCurrentPage,
  setActionPageSize,
  getActionData,
  createAction,
  updateAction,
  deleteAction,
  setSelectedActionData,
  //Action Points
  setActionPointsCurrentPage,
  setActionPointsPageSize,
  getActionPointsData,
  createActionPoints,
  updateActionPoints,
  deleteActionPoints,
  //Customer
  setCustomerActionCurrentPage,
  setCustomerActionPageSize,
  getAllPublicActions,
  getCustomerActionData,
  createCustomerAction,
  updateCustomerAction,
  deleteCustomerAction
};
